import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
	AppBar,
	Box,
	Button,
	Container,
	Drawer,
	IconButton,
	List,
	ListItem,
	Toolbar,
	Typography,
	useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { servicePages } from '../../servicePagesData';

const navigationItems = [
	{ label: 'Home', to: '/' },
	{ label: 'About', to: '/about' },
	{ label: 'Services', to: '/services' },
	{ label: 'Blog', to: '/blog' },
	{ label: 'Contact', to: '/contact' },
];

const Navbar = () => {
	const theme = useTheme();
	const location = useLocation();
	const [mobileOpen, setMobileOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 24);
		};

		handleScroll();
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);

	const isServiceDetailPage = servicePages.some((item) => item.path === location.pathname);
	const isActive = (to) => {
		if (to === '/') {
			return location.pathname === '/';
		}

		if (to === '/services') {
			return location.pathname.startsWith('/services') || isServiceDetailPage;
		}

		return location.pathname.startsWith(to);
	};

	const navButtonStyles = (active) => ({
		color: active ? 'text.primary' : 'text.secondary',
		px: 1.4,
		py: 0.75,
		borderRadius: '999px',
		fontWeight: active ? 600 : 500,
		backgroundColor: active ? alpha(theme.palette.common.white, 0.08) : 'transparent',
		'&:hover': {
			backgroundColor: alpha(theme.palette.common.white, 0.06),
			color: 'text.primary',
		},
	});

	return (
		<>
			<AppBar
				position="fixed"
				elevation={0}
				sx={{
					backgroundColor: scrolled ? alpha('#07111f', 0.76) : 'transparent',
					backdropFilter: scrolled ? 'blur(18px) saturate(160%)' : 'none',
					borderBottom: scrolled ? `1px solid ${alpha('#e2e8f0', 0.08)}` : '1px solid transparent',
					boxShadow: scrolled ? '0 16px 50px rgba(2, 6, 23, 0.24)' : 'none',
					transition: 'all 180ms ease',
				}}
			>
				<Container maxWidth="xl">
					<Toolbar
						sx={{
							minHeight: { xs: '82px !important', md: '96px !important' },
							px: { xs: 0, md: 1 },
						}}
					>
						<Box
							component={Link}
							to="/"
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.6,
								flexGrow: 1,
								minWidth: 0,
							}}
						>
							<Box
								sx={{
									width: 46,
									height: 46,
									borderRadius: '16px',
									display: 'grid',
									placeItems: 'center',
									background: 'linear-gradient(135deg, rgba(37,99,235,0.28) 0%, rgba(20,184,166,0.18) 100%)',
									border: `1px solid ${alpha('#e2e8f0', 0.1)}`,
									boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
								}}
							>
								<img src="/favicon.svg" alt="BrokenCosmos Labs logo" width="26" height="26" />
							</Box>
							<Box>
								<Typography variant="h6" sx={{ lineHeight: 1, fontWeight: 700 }}>
									BrokenCosmos Labs
								</Typography>
								<Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1, color: 'text.secondary' }}>
									Software systems that scale cleanly
								</Typography>
							</Box>
						</Box>

						<Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
							{navigationItems.map((item) => (
								<Button
									key={item.label}
									component={Link}
									to={item.to}
									disableRipple
									sx={navButtonStyles(isActive(item.to))}
								>
									{item.label}
								</Button>
							))}
							<Button component={Link} to="/contact" variant="contained" color="primary" sx={{ ml: 1.5 }}>
								Start a Project
							</Button>
						</Box>

						<IconButton
							aria-label="Open navigation menu"
							onClick={() => setMobileOpen(true)}
							sx={{
								display: { xs: 'inline-flex', md: 'none' },
								color: 'text.primary',
								border: `1px solid ${alpha('#e2e8f0', 0.12)}`,
								backgroundColor: alpha('#0f172a', 0.5),
							}}
						>
							<MenuRoundedIcon />
						</IconButton>
					</Toolbar>
				</Container>
			</AppBar>

			<Drawer
				anchor="right"
				open={mobileOpen}
				onClose={() => setMobileOpen(false)}
				sx={{
					display: { xs: 'block', md: 'none' },
					'& .MuiDrawer-paper': {
						width: '100%',
						maxWidth: 420,
						backgroundColor: alpha('#08101d', 0.96),
						backgroundImage: 'linear-gradient(180deg, rgba(37,99,235,0.14) 0%, rgba(8,16,29,0.98) 36%)',
						borderLeft: `1px solid ${alpha('#e2e8f0', 0.08)}`,
						boxSizing: 'border-box',
						p: 3,
					},
				}}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
					<Typography variant="h6">Navigation</Typography>
					<IconButton aria-label="Close navigation menu" onClick={() => setMobileOpen(false)} sx={{ color: 'text.primary' }}>
						<CloseRoundedIcon />
					</IconButton>
				</Box>
				<List sx={{ p: 0 }}>
					{navigationItems.map((item) => (
						<ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
							<Button
								component={Link}
								to={item.to}
								fullWidth
								sx={{
									justifyContent: 'flex-start',
									py: 1.5,
									px: 2,
									borderRadius: '16px',
									...navButtonStyles(isActive(item.to)),
								}}
							>
								{item.label}
							</Button>
						</ListItem>
					))}
				</List>
				<Box
					sx={{
						mt: 'auto',
						p: 2.5,
						borderRadius: '20px',
						backgroundColor: alpha('#0f172a', 0.72),
						border: `1px solid ${alpha('#e2e8f0', 0.08)}`,
					}}
				>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Need product engineering, modern infrastructure, or a cleaner client experience?
					</Typography>
					<Button component={Link} to="/contact" variant="contained" fullWidth>
						Book a Consultation
					</Button>
				</Box>
			</Drawer>
		</>
	);
};

export default Navbar;
